<script setup lang="ts">
import { computed, ref } from "vue";
import { NavArrowDown } from "@iconoir/vue";
import { AdminPermission } from "~~/schemas/admin-role";

const props = defineProps<{
  modelValue: string[];
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string[]): void;
}>();

const { t } = useI18n();

// Module groups mirror the route sections in artech-admin-api/main.go.
const PERM_GROUP_MAP: Record<string, string> = {
  workspaces: "workspace",
  "workspace-plans": "workspace",
  "workspace-orders": "workspace",
  analytics: "finance",
  costs: "finance",
  "unknown-transactions": "finance",
  users: "workspace",
  events: "workspace",
  orders: "workspace",
  leads: "workspace",
  "admin-users": "team",
  "admin-roles": "team",
  sites: "content",
  "site-templates": "content",
  "page-templates": "content",
  "widget-types": "content",
  assets: "content",
  apps: "content",
};

const GROUP_KEYS = ["workspace", "finance", "team", "content"];

function camelize(kebab: string): string {
  return kebab.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Group → resource → action checkboxes.
const groupedPermissions = computed(() => {
  const groups: Record<
    string,
    { order: string[]; resources: Record<string, { value: string; action: string }[]> }
  > = {};

  for (const value of Object.values(AdminPermission)) {
    const [resource, action] = value.split(":");
    const g = PERM_GROUP_MAP[resource] || "other";
    const grp = (groups[g] ||= { order: [], resources: {} });
    if (!grp.resources[resource]) {
      grp.resources[resource] = [];
      grp.order.push(resource);
    }
    grp.resources[resource].push({ value, action });
  }

  return GROUP_KEYS.filter((g) => groups[g]).map((g) => {
    const grp = groups[g];
    const groupLabel = t(`adminRole.permGroup${cap(g)}`);
    const resources = grp.order.map((resource) => {
      // Bare action labels — the resource sub-heading carries the context.
      const actions = grp.resources[resource].map((a) => ({
        value: a.value,
        action: a.action,
        label: t(`adminRole.act.${camelize(a.action)}`),
      }));
      return {
        resource,
        label: t(`adminRole.resource.${camelize(resource)}`),
        actions,
        values: actions.map((x) => x.value),
      };
    });
    return {
      key: g,
      label: groupLabel,
      // Sub-heading per resource, except the one that mirrors the group name.
      resources: resources.map((r) => ({
        ...r,
        showLabel: resources.length > 1 && r.label !== groupLabel,
      })),
      values: resources.flatMap((r) => r.values),
    };
  });
});

type PermissionGroup = (typeof groupedPermissions.value)[number];

const permStats = computed(() => {
  const all = groupedPermissions.value.flatMap((g) => g.values);
  return {
    granted: all.filter((v) => props.modelValue.includes(v)).length,
    total: all.length,
  };
});

const allSelectedState = computed((): boolean | "indeterminate" => {
  const { granted, total } = permStats.value;
  if (granted === 0) return false;
  if (granted === total) return true;
  return "indeterminate";
});

// Which module groups are expanded (accordion, flat list).
const expandedGroups = ref<string[]>([]);

const allExpanded = computed(
  () =>
    groupedPermissions.value.length > 0 &&
    groupedPermissions.value.every((g) => expandedGroups.value.includes(g.key)),
);

function isChecked(value: string): boolean {
  return props.modelValue.includes(value);
}

// A resource has any non-View action enabled → its View is auto-granted & locked.
function isForced(resource: string, action: string): boolean {
  if (action !== "read" || !isChecked(`${resource}:read`)) return false;
  return props.modelValue.some((p) => {
    const [r, a] = p.split(":");
    return r === resource && a !== "read";
  });
}

// Toggle one action. Enabling any action auto-enables View; clearing View
// clears the whole resource.
function toggleAction(resource: string, action: string) {
  const id = `${resource}:${action}`;
  let perms = [...props.modelValue];
  if (perms.includes(id)) {
    perms = perms.filter((p) => p !== id);
    if (action === "read") perms = perms.filter((p) => p.split(":")[0] !== resource);
  } else {
    perms.push(id);
    if (action !== "read" && !perms.includes(`${resource}:read`)) perms.push(`${resource}:read`);
  }
  emit("update:modelValue", perms);
}

function selectedCount(group: PermissionGroup): number {
  return group.values.filter((v) => props.modelValue.includes(v)).length;
}

function sectionState(group: PermissionGroup): boolean | "indeterminate" {
  const n = selectedCount(group);
  if (n === 0) return false;
  if (n === group.values.length) return true;
  return "indeterminate";
}

function toggleGroupAll(group: PermissionGroup) {
  if (selectedCount(group) === group.values.length) {
    emit("update:modelValue", props.modelValue.filter((p) => !group.values.includes(p)));
    return;
  }
  emit("update:modelValue", [...new Set([...props.modelValue, ...group.values])]);
}

function toggleSelectAll() {
  const all = groupedPermissions.value.flatMap((g) => g.values);
  if (permStats.value.granted === all.length) {
    emit("update:modelValue", props.modelValue.filter((p) => !all.includes(p)));
    return;
  }
  emit("update:modelValue", [...new Set([...props.modelValue, ...all])]);
}

function toggleExpand(key: string) {
  expandedGroups.value = expandedGroups.value.includes(key)
    ? expandedGroups.value.filter((k) => k !== key)
    : [...expandedGroups.value, key];
}

function toggleExpandAll() {
  expandedGroups.value = allExpanded.value ? [] : groupedPermissions.value.map((g) => g.key);
}
</script>

<template>
  <div class="perm-list" data-testid="permission-picker">
    <!-- Select all + expand all -->
    <div class="perm-list__row perm-list__toolbar">
      <FormCheckbox
        class="min-w-0 flex-1"
        :model-value="allSelectedState"
        :disabled="disabled"
        :aria-label="t('adminRole.selectAll')"
        @update:model-value="toggleSelectAll"
      >
        <template #label>
          <span class="text-body-secondary block truncate">
            {{ t("adminRole.permissionsSelected", { selected: permStats.granted, total: permStats.total }) }}
          </span>
        </template>
      </FormCheckbox>
      <Button
        variant="text-accent"
        size="small"
        class="shrink-0 whitespace-nowrap"
        @click="toggleExpandAll"
      >
        {{ allExpanded ? t("adminRole.collapseAll") : t("adminRole.expandAll") }}
      </Button>
    </div>

    <!-- Module groups -->
    <div
      v-for="group in groupedPermissions"
      :key="group.key"
      class="perm-group"
      :class="{ 'is-open': expandedGroups.includes(group.key) }"
    >
      <div class="perm-list__row perm-group__header" @click="toggleExpand(group.key)">
        <span class="inline-flex items-center" @click.stop>
          <FormCheckbox
            :model-value="sectionState(group)"
            :disabled="disabled"
            label-position="none"
            :aria-label="group.label"
            @update:model-value="toggleGroupAll(group)"
          />
        </span>
        <span class="text-body-secondary flex-1 min-w-0">{{ group.label }}</span>
        <span class="text-body-small-tertiary content-neutral-light shrink-0">
          {{ selectedCount(group) }}/{{ group.values.length }}
        </span>
        <NavArrowDown
          class="icon icon-small content-neutral-light shrink-0 perm-group__chevron"
          :class="{ 'is-open': expandedGroups.includes(group.key) }"
        />
      </div>

      <div class="perm-group__reveal">
        <div class="perm-group__body">
          <div class="perm-group__body-inner">
            <div v-for="res in group.resources" :key="res.resource" class="flex flex-col gap-small">
              <span v-if="res.showLabel" class="text-body-small-secondary">{{ res.label }}</span>
              <FormCheckbox
                v-for="a in res.actions"
                :key="a.value"
                :model-value="isChecked(a.value)"
                :disabled="disabled || isForced(res.resource, a.action)"
                :label="a.label"
                @update:model-value="toggleAction(res.resource, a.action)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Flat permission list: one bordered container, rows divided by full-width
// borders, group headers on a subtle surface, expanded body indented.
$divider-light: token("color-border-neutral-alpha-light-default");

.perm-list {
  border: 1px solid $divider-light;
  border-radius: token("corner-radius-xsmall");

  &__row {
    display: flex;
    align-items: center;
    gap: token("gap-small");
    padding: token("padding-small") token("padding-medium");
    border-bottom: 1px solid $divider-light;
  }

  &__toolbar {
    justify-content: space-between;
    background: token("color-bg-surface-alt");
  }
}

.perm-group {
  border-bottom: 1px solid $divider-light;

  &:last-child {
    border-bottom: none;
  }

  &__header {
    border-bottom: none;
    cursor: pointer;

    &:hover {
      background: token("color-bg-fill-neutral-flat-hover");
    }
  }

  // Only an expanded group gets the surface highlight + header divider.
  &.is-open > .perm-group__header {
    background: token("color-bg-surface-alt");
    border-bottom: 1px solid $divider-light;
  }

  // Smooth height reveal via the grid-rows 0fr → 1fr trick + motion tokens.
  &__reveal {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows token("motion-duration-standard") token("motion-ease-standard");
  }

  &.is-open > .perm-group__reveal {
    grid-template-rows: 1fr;
  }

  // The collapsing grid item itself carries no size — padding lives on the
  // inner wrapper so 0fr can shrink it fully to zero.
  &__body {
    overflow: hidden;
    min-height: 0;
  }

  &__body-inner {
    display: flex;
    flex-direction: column;
    gap: token("gap-medium");
    // Align body content with the group header label (past the checkbox + gap).
    padding: token("padding-medium") token("padding-medium") token("padding-medium")
      calc(#{token("padding-medium")} + #{token("comp-size-visual-2xsmall")} + #{token("gap-small")});
    opacity: 0;
    transition: opacity token("motion-duration-standard") token("motion-ease-standard");
  }

  &.is-open > .perm-group__reveal > .perm-group__body > .perm-group__body-inner {
    opacity: 1;
  }

  &__chevron {
    transition: transform token("motion-duration-fast") token("motion-ease-standard");

    &.is-open {
      transform: rotate(180deg);
    }
  }
}
</style>
